import { useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";
import { configOptions, DEVICE_LOCATION_OPTIONS } from "@/lib/config-options";
import { formatRelative } from "@/lib/format";
import { DeviceCameraCheck } from "@/components/admin/device-camera-check";
import { DeviceDetails } from "@/components/admin/device-details";
import { DevicePath } from "@/components/admin/device-path";
import { DeviceEnrolment } from "@/components/admin/device-enrolment";
import { useLastEvents, weights, type LastEvent } from "@/components/admin/device-last-events";
import { Pagination, Table, Td, type Column } from "@/components/admin/table";
import { usePaged } from "@/components/admin/use-paged";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/**
 * POS2-1354 CP1 — every device on the platform, and the commissioning screen.
 *
 * A new device enrols into the commissioning kitchen (POS2-1385), is tested there, and is
 * assigned to a customer from here. "Return to office" is the same assign, to the
 * commissioning kitchen. Staff-only and English, like every `/admin/*` page.
 */

interface AdminDevice {
  id: string;
  device_serial: string;
  name: string | null;
  status: string;
  location: string | null;
  retired: boolean;
  /** Live, but its kitchen, site, account or org is deleted: refused until moved. */
  kitchen_gone: boolean;
  org_id: string;
  org_name: string;
  site_id: string;
  site_name: string;
  kitchen_id: string;
  kitchen_name: string;
  is_commissioning: boolean;
  assigned_at: string;
  has_bounding_area: boolean;
  balena_uuid: string | null;
  last_capture_at: string | null;
  last_source: "device" | "imported" | null;
  last_device_at: string | null;
  last_imported_at: string | null;
  last_seen_at: string | null;
  queue_depth: number | null;
  /** Captures stored ahead of their photo and still held: unclassified, not in Transactions. */
  pending_photos: number;
  oldest_pending_at: string | null;
}

interface AdminDeviceList {
  devices: AdminDevice[];
  commissioning_kitchen: { id: string; name: string } | null;
}

interface AdminKitchen {
  id: string;
  name: string;
  site_id: string;
  site_name: string;
  org_id: string;
  org_name: string;
  is_commissioning: boolean;
}

/** Where a move sends the device. `commissioning` decides what happens to its queue. */
interface Target {
  kitchenId: string;
  label: string;
  commissioning: boolean;
}

const ALL = "all";

const COLUMNS: Column[] = [
  { label: "Device" },
  { label: "Site" },
  { label: "Kitchen" },
  { label: "Location" },
  { label: "Status" },
  { label: "Path" },
  { label: "Last capture" },
  { label: "Box" },
  { label: "Last seen" },
];

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

/** The list's time, or the event's own time with its weights once read (POS2-1436). */
function capture(listedAt: string | null, event: LastEvent | null | undefined): string {
  return event ? `${formatTime(event.logged_at)} · ${weights(event)}` : formatTime(listedAt);
}

export default function AdminDevicesPage() {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  // The labels Settings shows, so the two pages cannot name a location differently.
  const locationLabel = new Map(
    configOptions(t, DEVICE_LOCATION_OPTIONS).map(({ value, label }) => [value, label]),
  );
  const [search, setSearch] = useState("");
  const [orgId, setOrgId] = useState(ALL);
  const [siteId, setSiteId] = useState(ALL);
  const [commissioningOnly, setCommissioningOnly] = useState(false);
  const [noBox, setNoBox] = useState(false);
  const [notCutOver, setNotCutOver] = useState(false);
  const [photosPending, setPhotosPending] = useState(false);
  // `?device=<id>` opens that device's sheet: the Sites page links here (POS2-1383).
  const [params] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(params.get("device"));

  const list = useQuery<AdminDeviceList, Error>({
    queryKey: ["admin", "devices"],
    queryFn: () =>
      fetchJson<AdminDeviceList>(`${API_URL}/api/v1/admin/devices?include_retired=true`, headers),
    retry: false,
  });

  // Everything, retired included: an admin page should show the whole estate. Retired devices go
  // last, after the commissioning queue and the live fleet, in the server's order otherwise.
  const all = list.data?.devices ?? [];
  const devices = [...all.filter((d) => !d.retired), ...all.filter((d) => d.retired)];
  const orgs = [...new Map(devices.map((d) => [d.org_id, d.org_name])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  );
  const sites = [
    ...new Map(
      devices.filter((d) => d.org_id === orgId).map((d) => [d.site_id, d.site_name]),
    ).entries(),
  ].sort((a, b) => a[1].localeCompare(b[1]));

  const needle = search.trim().toLowerCase();
  const shown = devices.filter(
    (d) =>
      (!needle ||
        d.device_serial.toLowerCase().includes(needle) ||
        (d.name ?? "").toLowerCase().includes(needle)) &&
      (orgId === ALL || d.org_id === orgId) &&
      (siteId === ALL || d.site_id === siteId) &&
      (!commissioningOnly || d.is_commissioning) &&
      (!noBox || !d.has_bounding_area) &&
      (!notCutOver || d.last_device_at === null) &&
      (!photosPending || d.pending_photos > 0),
  );
  const paged = usePaged(shown);
  const inCommissioning = devices.filter((d) => d.is_commissioning && !d.retired).length;
  const retired = devices.filter((d) => d.retired).length;
  const selected = devices.find((d) => d.id === selectedId) ?? null;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="font-display text-xl font-extrabold tracking-tight text-gray-900">
          Devices
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-testid="devices-summary">
          {shown.length === devices.length ? (
            <>
              All devices · all organisations · <b className="text-gray-700">{devices.length}</b>{" "}
              devices
            </>
          ) : (
            <>
              Filtered · showing <b className="text-gray-700">{shown.length}</b> of {devices.length}{" "}
              devices
            </>
          )}{" "}
          · <b className="text-gray-700">{inCommissioning}</b> in commissioning ·{" "}
          <b className="text-gray-700">{retired}</b> retired
        </p>
      </div>

      {list.data && !list.data.commissioning_kitchen && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          No kitchen is flagged as the commissioning kitchen. New devices cannot enrol (503) and
          Return to office has nowhere to go. Flag one by hand (RUNBOOK → Device commissioning and
          enrolment).
        </p>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-300" />
          <Input
            aria-label="Search devices"
            placeholder="Serial or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={orgId}
          onValueChange={(v) => {
            setOrgId(v);
            setSiteId(ALL);
          }}
        >
          <SelectTrigger className="w-56" aria-label="Organisation">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All organisations</SelectItem>
            {orgs.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={siteId} onValueChange={setSiteId} disabled={orgId === ALL}>
          <SelectTrigger className="w-56" aria-label="Site">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All sites</SelectItem>
            {sites.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <Toggle
          label="Commissioning only"
          checked={commissioningOnly}
          onChange={setCommissioningOnly}
        />
        <Toggle label="No bounding box" checked={noBox} onChange={setNoBox} />
        <Toggle label="Not cut over" checked={notCutOver} onChange={setNotCutOver} />
        <Toggle label="Photos pending" checked={photosPending} onChange={setPhotosPending} />
      </div>

      {list.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {errorDetail(list.error)}
        </p>
      ) : (
        <Table<AdminDevice>
          columns={COLUMNS}
          rows={paged.page}
          isLoading={list.isLoading}
          footer={paged.bar ? <Pagination {...paged.bar} /> : undefined}
          empty={devices.length ? "No devices match these filters." : "No devices."}
          renderRow={(device, columns) => (
            <tr
              key={device.id}
              role="button"
              tabIndex={0}
              data-testid={`device-row-${device.device_serial}`}
              className={`cursor-pointer border-b border-black/[0.06] last:border-b-0 hover:bg-gray-50 ${
                device.retired ? "opacity-60" : ""
              }`}
              onClick={() => setSelectedId(device.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSelectedId(device.id);
              }}
            >
              <Td column={columns[0]}>
                <span className="block font-mono font-medium text-gray-900">
                  {device.device_serial}
                </span>
                {device.name && <span className="block text-xs text-gray-400">{device.name}</span>}
              </Td>
              <Td column={columns[1]}>
                <span className="block text-gray-900">{device.site_name}</span>
                <span className="block text-xs text-gray-400">{device.org_name}</span>
              </Td>
              <Td column={columns[2]}>
                <span className="block">{device.kitchen_name}</span>
                {device.is_commissioning && (
                  <Badge variant="secondary" className="mt-1">
                    Commissioning
                  </Badge>
                )}
              </Td>
              <Td column={columns[3]}>
                {device.location ? (
                  locationLabel.get(device.location)
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </Td>
              <Td column={columns[4]}>
                {device.retired ? (
                  <Badge variant="outline">Retired</Badge>
                ) : device.kitchen_gone ? (
                  <Badge variant="destructive">Kitchen deleted</Badge>
                ) : (
                  device.status
                )}
              </Td>
              <Td column={columns[5]}>
                <DevicePath device={device} />
                {device.pending_photos > 0 && (
                  <Badge
                    variant="outline"
                    className="mt-1 block w-fit border-amber-300 text-amber-700"
                  >
                    {pendingLabel(device.pending_photos)}
                  </Badge>
                )}
              </Td>
              <Td column={columns[6]}>{formatTime(device.last_capture_at)}</Td>
              <Td column={columns[7]}>
                {device.has_bounding_area ? "Set" : <span className="text-gray-300">—</span>}
              </Td>
              <Td column={columns[8]}>{formatTime(device.last_seen_at)}</Td>
            </tr>
          )}
        />
      )}

      <Sheet open={selected !== null} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {selected && (
            <DeviceSheet
              // Keyed on the kitchen too, so a completed move starts the picker again: the old
              // choice would otherwise leave Assign enabled for a second, unintended move.
              key={`${selected.id}:${selected.kitchen_id}`}
              device={selected}
              commissioningKitchen={list.data?.commissioning_kitchen ?? null}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      {label}
    </label>
  );
}

/** POS2-1399: the weights are in, the photos are still on the device. */
function pendingLabel(count: number): string {
  return `${count} ${count === 1 ? "photo" : "photos"} pending`;
}

function DeviceSheet({
  device,
  commissioningKitchen,
}: {
  device: AdminDevice;
  commissioningKitchen: { id: string; name: string } | null;
}) {
  const headers = useApiHeaders();
  const lastEvents = useLastEvents(device);
  const [orgId, setOrgId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [kitchenId, setKitchenId] = useState("");
  const [target, setTarget] = useState<Target | null>(null);

  const kitchens = useQuery<AdminKitchen[], Error>({
    queryKey: ["admin", "devices", "kitchens"],
    queryFn: () => fetchJson<AdminKitchen[]>(`${API_URL}/api/v1/admin/devices/kitchens`, headers),
    retry: false,
  });

  const all = kitchens.data ?? [];
  const orgs = [...new Map(all.map((k) => [k.org_id, k.org_name])).entries()];
  const sites = [
    ...new Map(
      all.filter((k) => k.org_id === orgId).map((k) => [k.site_id, k.site_name]),
    ).entries(),
  ];
  const siteKitchens = all.filter((k) => k.site_id === siteId);
  const picked = all.find((k) => k.id === kitchenId) ?? null;

  function pickSite(id: string) {
    setSiteId(id);
    const inSite = all.filter((k) => k.site_id === id);
    // `kitchen_id` is the real key, so a site with one kitchen needs no second choice.
    setKitchenId(inSite.length === 1 ? inSite[0].id : "");
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="font-mono">{device.device_serial}</SheetTitle>
        <SheetDescription>
          {device.org_name} · {device.site_name} · {device.kitchen_name}
        </SheetDescription>
      </SheetHeader>
      <div className="space-y-6 px-4 pb-6 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <Detail term="Organisation" value={device.org_name} />
          <Detail term="Site" value={device.site_name} />
          <Detail term="Kitchen" value={device.kitchen_name} />
          <Detail term="Status" value={device.retired ? "Retired" : device.status} />
          <Detail term="Assigned" value={formatTime(device.assigned_at)} />
          <Detail term="Balena device" value={device.balena_uuid ?? "—"} />
          <Detail
            term="Last direct capture"
            value={capture(device.last_device_at, lastEvents.data?.device)}
          />
          <Detail
            term="Last DataBridge capture"
            value={capture(device.last_imported_at, lastEvents.data?.imported)}
          />
          {lastEvents.isError && <Detail term="Weights" value={errorDetail(lastEvents.error)} />}
          <Detail term="Last heartbeat" value={formatTime(device.last_seen_at)} />
          <Detail
            term="Queued captures"
            value={device.queue_depth === null ? "—" : String(device.queue_depth)}
          />
          <Detail
            term="Pending photos"
            value={
              device.oldest_pending_at === null
                ? "None"
                : `${pendingLabel(device.pending_photos)}, oldest ${formatRelative("en", device.oldest_pending_at)}`
            }
          />
        </dl>

        {device.kitchen_gone && (
          <p role="alert" className="text-red-700">
            {device.kitchen_name}, or the site, account or organisation above it, is deleted. The
            device is refused until it is assigned to a live kitchen.
          </p>
        )}
        {!device.retired && (
          // Keyed on the saved values, so a save or a refetch starts the form again from them.
          <DeviceDetails
            key={`${device.id}:${device.name}:${device.location}:${device.status}`}
            device={device}
          />
        )}
        {!device.retired && <DeviceCameraCheck deviceId={device.id} />}
        <DeviceEnrolment
          deviceId={device.id}
          serial={device.device_serial}
          retired={device.retired}
        />

        {device.retired ? (
          <p className="text-gray-500">A retired device cannot be moved.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">Assign</p>
            {kitchens.isError && <p className="text-red-700">{errorDetail(kitchens.error)}</p>}
            <Select
              value={orgId}
              onValueChange={(v) => {
                setOrgId(v);
                setSiteId("");
                setKitchenId("");
              }}
            >
              <SelectTrigger className="w-full" aria-label="Target organisation">
                <SelectValue placeholder="Organisation" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={siteId} onValueChange={pickSite} disabled={!orgId}>
              <SelectTrigger className="w-full" aria-label="Target site">
                <SelectValue placeholder="Site" />
              </SelectTrigger>
              <SelectContent>
                {sites.map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kitchenId} onValueChange={setKitchenId} disabled={!siteId}>
              <SelectTrigger className="w-full" aria-label="Target kitchen">
                <SelectValue placeholder="Kitchen" />
              </SelectTrigger>
              <SelectContent>
                {siteKitchens.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!picked || picked.id === device.kitchen_id}
                onClick={() =>
                  picked &&
                  setTarget({
                    kitchenId: picked.id,
                    label: `${picked.org_name} · ${picked.site_name} · ${picked.name}`,
                    commissioning: picked.is_commissioning,
                  })
                }
              >
                Assign
              </Button>
              {commissioningKitchen && !device.is_commissioning && (
                <Button
                  variant="outline"
                  onClick={() =>
                    setTarget({
                      kitchenId: commissioningKitchen.id,
                      label: commissioningKitchen.name,
                      commissioning: true,
                    })
                  }
                >
                  Return to office
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {target && <AssignDialog device={device} target={target} onClose={() => setTarget(null)} />}
    </>
  );
}

function Detail({ term, value }: { term: string; value: string }) {
  return (
    <>
      <dt className="text-gray-400">{term}</dt>
      <dd className="text-gray-900">{value}</dd>
    </>
  );
}

/**
 * What the move does to the device's queued captures: out of the office the device drops
 * them, and otherwise one logged before the move is parked in the commissioning kitchen
 * (POS2-1387).
 */
function AssignDialog({
  device,
  target,
  onClose,
}: {
  device: AdminDevice;
  target: Target;
  onClose: () => void;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const assign = useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/devices/${device.id}/assign`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ kitchen_id: target.kitchenId }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "devices"] });
      onClose();
    },
  });

  const customerToCustomer = !device.is_commissioning && !target.commissioning;
  const queued = device.queue_depth ?? 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {target.commissioning ? "Return to office" : "Assign"} {device.device_serial}
          </DialogTitle>
          <DialogDescription>To {target.label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-gray-600">
          {device.is_commissioning && (
            <p>
              Captures the device logged in the office, before this move, are dropped on the device.
              Later captures go to the new kitchen.
            </p>
          )}
          {customerToCustomer && (
            <p>
              Captures still queued on the device are parked in the commissioning kitchen, in
              Positive Carbon&apos;s own organisation, to be re-filed by hand. They are not written
              into the new kitchen.
            </p>
          )}
          {customerToCustomer && device.queue_depth === null && (
            <p className="text-amber-700">
              The device has sent no heartbeat, so its queue depth is unknown.
            </p>
          )}
          {customerToCustomer && queued > 0 && (
            <p role="alert" className="font-medium text-red-700">
              At its last heartbeat ({formatTime(device.last_seen_at)}) the device had {queued}{" "}
              queued {queued === 1 ? "capture" : "captures"}. Wait for the queue to drain, or they
              will be parked rather than reach {target.label}.
            </p>
          )}
          {target.commissioning && (
            <p>
              Captures still queued on the device go to the commissioning kitchen, in Positive
              Carbon&apos;s own organisation, where they can be recovered.
            </p>
          )}
          <p>
            Events already written stay where they were captured. The device picks up the move
            within {device.is_commissioning ? "a minute" : "an hour"}.
          </p>
          {assign.isError && <p className="text-red-700">{errorDetail(assign.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => assign.mutate()} disabled={assign.isPending}>
            {target.commissioning ? "Return to office" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
